// Cinematic startup sequence — driven by /stores/introStore state machine.
// Phases: BLACK_SCREEN → AXIS_INTRO → BLACK_TRANSITION → GAME_TITLE_INTRO
//          → BLACK_TRANSITION_2 → MENU_LOADING → DONE
//
// - First-time viewers cannot skip during the initial grace window.
// - Subsequent launches allow Space / Enter / Escape / click to skip.
// - Setting "Play startup intro on launch" (settingsStore) fully bypasses this.
// - Audio hooks are per-phase and individually replaceable.
import { useEffect, useMemo, useRef, useState } from "react";
import { useGameStore, SCREEN } from "@/stores/gameStore";
import { useIntroStore, PHASE, hasSeenIntro } from "@/stores/introStore";
import { Audio_ } from "@/lib/audio";
import { INTRO_TIMING, SKIP_GRACE_MS } from "@/config/game";
import { INTRO } from "@/constants/testIds";
import AxisLogoIntro from "@/components/AxisWordmark";
import CrossfireTitle from "@/components/CrossfireTitle";

export default function IntroSequence() {
    const goto = useGameStore((s) => s.goto);
    const phase = useIntroStore((s) => s.phase);
    const phaseIndex = useIntroStore((s) => s.phaseIndex);
    const canSkip = useIntroStore((s) => s.canSkip);
    const finished = useIntroStore((s) => s.finished);
    const start = useIntroStore((s) => s.start);
    const advance = useIntroStore((s) => s.advance);
    const requestSkip = useIntroStore((s) => s.requestSkip);
    const unlockSkip = useIntroStore((s) => s.unlockSkip);

    const [showSkipHint, setShowSkipHint] = useState(false);
    const startedRef = useRef(false);

    // Boot the state machine once
    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;
        start();
    }, [start]);

    // Timer-driven phase advance. Uses a single timeout per phase so we
    // cannot accidentally double-fire or skip past a phase.
    useEffect(() => {
        if (finished) return;
        const duration = getPhaseDuration(phase);
        if (duration <= 0) return;
        const t = setTimeout(() => advance(), duration);
        return () => clearTimeout(t);
    }, [phase, phaseIndex, finished, advance]);

    // Trigger per-phase audio
    useEffect(() => {
        switch (phase) {
            case PHASE.AXIS_INTRO:
                Audio_.play("axisIn", { category: "music" });
                // Low-freq impact when the logo is fully visible
                setTimeout(
                    () => Audio_.play("axisImpact", { category: "music" }),
                    INTRO_TIMING.AXIS_INTRO_IN - 60
                );
                setTimeout(
                    () => Audio_.play("axisOut", { category: "music" }),
                    INTRO_TIMING.AXIS_INTRO_IN + INTRO_TIMING.AXIS_INTRO_HOLD
                );
                break;
            case PHASE.BLACK_TRANSITION:
            case PHASE.BLACK_TRANSITION_2:
                Audio_.play("blackTransition", { category: "music" });
                break;
            case PHASE.GAME_TITLE_INTRO:
                Audio_.play("titleIn", { category: "music" });
                setTimeout(
                    () => Audio_.play("titleImpact", { category: "music" }),
                    INTRO_TIMING.GAME_TITLE_IN - 120
                );
                break;
            case PHASE.MENU_LOADING:
                Audio_.play("menuEnter", { category: "music" });
                break;
            default:
                break;
        }
    }, [phase]);

    // Hand off to main menu when done
    useEffect(() => {
        if (phase === PHASE.DONE) {
            goto(SCREEN.MENU);
        }
    }, [phase, goto]);

    // Unlock skip after the grace window on first watch; show skip hint
    useEffect(() => {
        const already = hasSeenIntro();
        if (already) {
            // Show hint immediately (small delay)
            const t = setTimeout(() => setShowSkipHint(true), 400);
            return () => clearTimeout(t);
        }
        const t = setTimeout(() => {
            unlockSkip();
            setShowSkipHint(true);
        }, SKIP_GRACE_MS);
        return () => clearTimeout(t);
    }, [unlockSkip]);

    // Skip via keyboard / click
    useEffect(() => {
        const onKey = (e) => {
            if (["Space", "Enter", "Escape"].includes(e.code)) requestSkip();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [requestSkip]);

    const axisPhase = useMemo(() => mapAxisPhase(phase), [phase]);
    const titlePhase = useMemo(() => mapTitlePhase(phase), [phase]);
    const blackness = getBlackness(phase);

    return (
        <div
            data-testid={INTRO.root}
            className="fixed inset-0 overflow-hidden select-none"
            style={{ background: "#000000" }}
            onClick={() => requestSkip()}
        >
            {/* Persistent AXIS card — mounted while relevant to keep transitions stable */}
            {(phase === PHASE.BLACK_SCREEN || phase === PHASE.AXIS_INTRO) && (
                <StageCentered>
                    <AxisLogoIntro phase={axisPhase} />
                </StageCentered>
            )}

            {/* Game title composition */}
            {(phase === PHASE.BLACK_TRANSITION ||
                phase === PHASE.GAME_TITLE_INTRO ||
                phase === PHASE.BLACK_TRANSITION_2) && (
                <CrossfireTitle phase={titlePhase} />
            )}

            {/* Menu loading — layered reveal of the target menu */}
            {phase === PHASE.MENU_LOADING && <MenuLoading />}

            {/* Cinematic bars */}
            <div className="pointer-events-none absolute left-0 right-0 top-0 h-16 bg-black z-30" />
            <div className="pointer-events-none absolute left-0 right-0 bottom-0 h-16 bg-black z-30" />

            {/* Blackness overlay for smooth transitions */}
            <div
                className="pointer-events-none absolute inset-0 z-40 bg-black"
                style={{
                    opacity: blackness,
                    transition: "opacity 900ms ease",
                }}
            />

            {/* Skip hint */}
            {showSkipHint && canSkip && !finished && phase !== PHASE.MENU_LOADING && (
                <button
                    data-testid={INTRO.skip}
                    onClick={(e) => {
                        e.stopPropagation();
                        requestSkip();
                    }}
                    className="absolute bottom-6 right-8 z-50 flex items-center gap-3 axis-cut-sm px-3 py-2 border border-white/15 hover:border-white/40 bg-black/40 transition"
                    style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 10,
                        letterSpacing: "0.4em",
                        color: "rgba(255,255,255,0.55)",
                    }}
                >
                    SKIP INTRO ▶
                </button>
            )}
        </div>
    );
}

function StageCentered({ children }) {
    return (
        <div className="absolute inset-0 flex items-center justify-center z-10">
            {children}
        </div>
    );
}

function MenuLoading() {
    // Layered reveal: darkness lifts, "loading" strip flashes, then hand-off.
    return (
        <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
            <div
                className="axis-fade-in"
                style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10,
                    letterSpacing: "0.55em",
                    color: "rgba(255,255,255,0.55)",
                }}
            >
                ENTERING OPERATIONS
            </div>
            <div className="mt-4 h-px w-52 overflow-hidden bg-white/10">
                <div
                    style={{
                        height: "100%",
                        width: "40%",
                        background:
                            "linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent)",
                        animation: "menuLoad 900ms linear",
                    }}
                />
            </div>
            <style>{`
                @keyframes menuLoad {
                    from { transform: translateX(-100%); }
                    to { transform: translateX(300%); }
                }
            `}</style>
        </div>
    );
}

// --- Helpers ---

function getPhaseDuration(phase) {
    switch (phase) {
        case PHASE.BLACK_SCREEN:
            return INTRO_TIMING.BLACK_SCREEN;
        case PHASE.AXIS_INTRO:
            return (
                INTRO_TIMING.AXIS_INTRO_IN +
                INTRO_TIMING.AXIS_INTRO_HOLD +
                INTRO_TIMING.AXIS_INTRO_OUT
            );
        case PHASE.BLACK_TRANSITION:
            return INTRO_TIMING.BLACK_TRANSITION;
        case PHASE.GAME_TITLE_INTRO:
            return (
                INTRO_TIMING.GAME_TITLE_IN +
                INTRO_TIMING.GAME_TITLE_HOLD +
                INTRO_TIMING.GAME_TITLE_OUT
            );
        case PHASE.BLACK_TRANSITION_2:
            return INTRO_TIMING.BLACK_TRANSITION_2;
        case PHASE.MENU_LOADING:
            return INTRO_TIMING.MENU_LOADING;
        default:
            return 0;
    }
}

function mapAxisPhase(phase) {
    if (phase === PHASE.BLACK_SCREEN) return "in";
    if (phase === PHASE.AXIS_INTRO) return "hold";
    return "out";
}

function mapTitlePhase(phase) {
    if (phase === PHASE.BLACK_TRANSITION) return "in";
    if (phase === PHASE.GAME_TITLE_INTRO) return "hold";
    return "out";
}

function getBlackness(phase) {
    switch (phase) {
        case PHASE.BLACK_SCREEN:
        case PHASE.BLACK_TRANSITION:
        case PHASE.BLACK_TRANSITION_2:
            return 1;
        case PHASE.MENU_LOADING:
            return 0.35;
        default:
            return 0;
    }
}
